import { EMPTY, concat, from, of } from 'rxjs';
import { catchError, filter, finalize, mergeMap, take, takeUntil } from 'rxjs/operators';
import get from 'lodash.get';
import isEmpty from 'lodash.isempty';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import {
  BULK_MULTI_PACKAGE_SET_SELECT_FROM_BULK_MODAL,
  BULK_MULTI_PACKAGE_SET_SELECT_FROM_ORDERS_DRAWER,
} from '../../../constants/analytics-events';
import Global from '../../global-selectors';
import { BulkUpdateFeedbackModal } from '../../../components/modals/bulk-update-feedback-modal';
import { ErrorModal } from '../../../components/modals/error-modal';
import { LaunchBulkUpdateRetryModal$ } from './launch-bulk-update-retry-modal';
import { Segment } from '../../../services/analytics-services/segment';
import { BulkUpdateEvents } from '../../../tracking/tracking-constants';

import { ModalService } from 'ui-components/services/modal-service';
import { NotificationService } from 'ui-components/services/notification-service';
import { BulkActionService } from '../../../services/bulk-action-service';
import { BulkActionJobValidator } from '../../../services/bulk-action-job-validator';
import { getIsBatchDetailsPage } from '../../batches/batches.helpers';

import { reloadBatchDetails } from '../../batches/details/batch.details.actions';
import * as DrawerActions from '../../orders/drawer/orders.drawer.actions';
import { orderGridGetRates } from '../../orders/grid/orders.grid.header.actions';

import * as Actions from './bulk.update.actions';
import { messages } from './messages';
import { BulkActionRequestHandlers } from './bulk-action-request-handlers';
import { bulkUpdateJobDeleted$ } from './bulk.update.observables';
import { createEventBindingsWithFallback, getIsJobComplete } from './pusher-helpers';
import { CountryConfigNames } from '../../../contexts/country-config-context/country-config-enums';

const getUpdateRequestBasedOnBulkActions = (bulkActions) => {
  const defaultHandler = BulkActionRequestHandlers.find((handler) => handler.id === 'shipping-configuration').handler;
  if (bulkActions.length !== 1) {
    return defaultHandler;
  }
  const firstBulkAction = bulkActions[0];
  const matchingBulkActionRequestHandler = BulkActionRequestHandlers.find((handler) =>
    handler.applicableBulkActions.find((bulkAction) => bulkAction.name === firstBulkAction.name),
  );
  return get(matchingBulkActionRequestHandler, 'handler', defaultHandler);
};

const threeSeconds = 3000;

const PACKAGE_SET_UPDATE_SOURCE = {
  MODAL: 'modal',
  DRAWER: 'drawer',
};

const PACKAGE_SOURCE_EVENTS = {
  [PACKAGE_SET_UPDATE_SOURCE.MODAL]: BULK_MULTI_PACKAGE_SET_SELECT_FROM_BULK_MODAL,
  [PACKAGE_SET_UPDATE_SOURCE.DRAWER]: BULK_MULTI_PACKAGE_SET_SELECT_FROM_ORDERS_DRAWER,
};

const trackPackageSetUpdate = (succeeded, job, processedCount, source) => {
  if (!succeeded) return;

  const { bulkActions, model } = job;
  const isPackageAction = bulkActions.some((action) => action.name === 'packageBulkAction');
  const { packageSetId, packageSetName } = model?.shippingConfiguration || {};

  if (!isPackageAction || !packageSetId) return;

  const packages = model?.shippingConfiguration?.packages || [];

  const eventParams = {
    packageSetId,
    packageSetName,
    packageCount: packages.length,
    affectedOrdersCount: processedCount || 0,
  };

  const eventType = PACKAGE_SOURCE_EVENTS[source];
  if (eventType) {
    Segment.trackEvent(eventType, eventParams);
  }
};

const handleUpdateSuccess$ = (pusherMsg$, getState, jobGuid, job, source) => {
  const { id, bulkUpdateTypeData } = job;

  const content = bulkUpdateTypeData.startUpdateNotificationContent(job);

  const notificationId = NotificationService.showNotification({
    title: <FormattedMessage {...messages.bulkUpdating} />,
    content,
    notificationType: 'info',
    progress: true,
    dontHide: true,
  });

  return concat(
    of(Actions.bulkUpdateRequestSuccess(id)),
    pusherMsg$.pipe(
      filter((pusherMsg) => {
        return pusherMsg.messageGuid === jobGuid && getIsJobComplete(pusherMsg);
      }),
      take(1),
      mergeMap(({ data }) => {
        const { succeeded, processedCount, skippedCount, skippedMarketplaces } = data || {};

        const bulkActionResults = {
          processedCount,
          skippedCount,
          skippedMarketplaces,
        };

        trackPackageSetUpdate(succeeded, job, processedCount, source);

        if (succeeded) {
          const bulkActionNames = job.bulkActions.map((a) => a.name);
          const modelForTracking = { ...job.model };
          delete modelForTracking.errors;
          Segment.trackEvent(BulkUpdateEvents.BULK_UPDATE_COMPLETE, {
            bulkActions: bulkActionNames,
            processedCount,
            skippedCount,
            ...(job.source && { source: job.source }),
            model: modelForTracking,
          });
        }

        if (succeeded && bulkActionResults?.skippedCount > 0) {
          ModalService.launchModal({
            component: BulkUpdateFeedbackModal,
            props: { bulkActionResults },
          });
        }

        NotificationService.updateNotification(notificationId, {
          duration: threeSeconds,
          dontHide: false,
          title: succeeded ? <FormattedMessage {...messages.updateSuccessful} /> : <FormattedMessage {...messages.bulkUpdateFailed} />,
          content: succeeded ? (
            bulkUpdateTypeData.updateSuccessNotificationContent(job)
          ) : (
            <FormattedMessage {...messages.bulkUpdateFailedContent} />
          ),
          notificationType: succeeded ? 'success' : 'error',
          progress: false,
        });

        if (!succeeded) {
          return EMPTY;
        }

        const actions = [];
        const isBatchDetailsPage = getIsBatchDetailsPage();
        if (isBatchDetailsPage) {
          actions.push(reloadBatchDetails());
        }

        const dataTypeActions = bulkUpdateTypeData.updateSuccessActions(job, getState());
        dataTypeActions.forEach((act) => actions.push(act));

        actions.push(DrawerActions.getDrawerOrders());
        actions.push(orderGridGetRates());

        return concat(actions);
      }),
    ),
  );
};

export const bulkUpdateRequestFromModal$ = (getState, action, jobFromState, modalId) => {
  const { bulkActions } = jobFromState;

  const { pusherMsg$, jobGuidManager } = createEventBindingsWithFallback();
  const updateRequest$ = getUpdateRequestBasedOnBulkActions(bulkActions);
  return updateRequest$(jobFromState).pipe(
    mergeMap((apiResponseData) => {
      // we don't use multiple job id's anymore so even though it's in an array we only care about the first element
      const [jobGuid] = apiResponseData.jobGuids;

      jobGuidManager.addJobGuid(apiResponseData.jobGuids[0]);
      return handleUpdateSuccess$(pusherMsg$, getState, jobGuid, jobFromState, PACKAGE_SET_UPDATE_SOURCE.MODAL);
    }),
    finalize(() => ModalService.closeModalById(modalId)),
    catchError(() => LaunchBulkUpdateRetryModal$().pipe(mergeMap(() => of(action)))),
  );
};

export const bulkUpdateRequestFromDrawer$ = (getState, action, jobFromState) => {
  const { bulkActions, jobData } = jobFromState;
  const state = getState();
  const [, configData] = Global.selectCountryFeatureConfig(state, CountryConfigNames.ShippingStrategy);
  const { ignoreConfirmationBulkUpdate, ignorePackageBulkUpdate } = configData ?? {};
  return from(
    BulkActionJobValidator.validate(bulkActions, jobData, {
      ignoreConfirmationBulkUpdate,
      ignorePackageBulkUpdate,
    }),
  ).pipe(
    mergeMap(() => {
      const { pusherMsg$, jobGuidManager } = createEventBindingsWithFallback();
      const updateRequest$ = getUpdateRequestBasedOnBulkActions(bulkActions);
      return updateRequest$(jobFromState).pipe(
        mergeMap((apiResponseData) => {
          // we don't use multiple job id's anymore so even though it's in an array we only care about the first element
          const [jobGuid] = apiResponseData.jobGuids;

          jobGuidManager.addJobGuid(apiResponseData.jobGuids[0]);
          return handleUpdateSuccess$(pusherMsg$, getState, jobGuid, jobFromState, PACKAGE_SET_UPDATE_SOURCE.DRAWER);
        }),
        catchError(() => LaunchBulkUpdateRetryModal$().pipe(mergeMap(() => of(action)))),
      );
    }),
    catchError((validationError) => {
      ModalService.launchModal({
        component: ErrorModal,
        props: validationError,
      });
      return EMPTY;
    }),
  );
};

export const onValidateBulkUpdateJob$ = (action$, getState, bulkUpdateJobId, bulkUpdateRequest$, modalId) => {
  return action$.pipe(
    filter((action) => {
      return action.type === Actions.VALIDATE_BULK_UPDATE_JOB && action.bulkUpdateJobId === bulkUpdateJobId;
    }),
    mergeMap((action) => {
      const jobFromState = Global.selectBulkUpdateBulkUpdateJob(getState(), {
        bulkUpdateJobId,
      });
      const { model, bulkActions } = jobFromState;
      const errors = BulkActionService.getModelErrors(bulkActions, model);
      if (!isEmpty(errors)) {
        const newModel = {
          ...model,
          errors,
        };
        return of(Actions.setModel(bulkUpdateJobId, newModel));
      }

      return concat(
        of(Actions.validateBulkUpdateJobSuccess(bulkUpdateJobId, [])),
        bulkUpdateRequest$(getState, action, jobFromState, modalId),
      );
    }),
    takeUntil(bulkUpdateJobDeleted$(action$, bulkUpdateJobId)),
  );
};
